import { FC, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiChip, MiLoader, ZoomInput } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  LoaderType,
} from "@miplatform/types";
import { mdiFilterVariant } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useUnassignedWorkOrdersListViewModel } from "./hooks/useUnassignedWorkOrdersListViewModel";
import { CalendarFilterGroup } from "../CalendarToolBar/CalendarFilterGroup";
import { useCalendarFilterGroup } from "../CalendarToolBar/useCalendarFilterGroup";
import { UnassignedWorkOrdersList } from "./UnassignedWorkOrdersList";

const EMPTY_ATTEMPT_IDS: ReadonlySet<string> = new Set<string>();
const ZOOM_OPTIONS = [1, 2, 3, 4].map((value) => ({
  label: `Zoom ${Math.round(value * 100)}%`,
  value,
}));

export const UnassignedWorkOrders: FC<UnassignedWorkOrdersProps> = ({
  stagedAttemptIds = EMPTY_ATTEMPT_IDS,
  confirmedAttemptIds = EMPTY_ATTEMPT_IDS,
  onWorkOrderClick,
}) => {
  const { classes } = useStyles();
  const [listZoom, setListZoom] = useState(1);
  const {
    showFilters,
    toggleFilters,
    filters,
    appliedFilters,
    appliedFilterLabels,
    hasPendingFilters,
    handleApplyFilters,
    handleResetFilters,
    handleFilterChange,
    builderOptions,
    communityOptions,
    statusOptions,
  } = useCalendarFilterGroup();
  const { workOrders, isLoading } =
    useUnassignedWorkOrdersListViewModel(appliedFilters);

  const visibleWorkOrders = useMemo(
    () =>
      workOrders.filter(
        (wo) =>
          !stagedAttemptIds.has(wo.id) && !confirmedAttemptIds.has(wo.id)
      ),
    [workOrders, stagedAttemptIds, confirmedAttemptIds]
  );

  const visibleWorkOrdersCount = visibleWorkOrders.length;

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Box className={classes.titleGroup}>
          <Typography variant={TYPOGRAPHY_VARIANTS.HD2}>
            Unassigned Work Orders
          </Typography>
          {visibleWorkOrdersCount > 0 && (
            <MiChip
              label={String(visibleWorkOrdersCount)}
              size="small"
              className={classes.countChip}
            />
          )}
        </Box>
        <Box className={classes.controlsRow}>
          <ZoomInput
            value={listZoom}
            options={ZOOM_OPTIONS}
            handleChange={(event: SelectChangeEvent<number>) =>
              setListZoom(Number(event.target.value))
            }
            className={classes.zoomInput}
          />
          <MiButton
            title="Filter"
            onClick={toggleFilters}
            startIcon={{
              path: mdiFilterVariant,
            }}
            variant={BUTTON_VARIANTS.OUTLINED}
            color={BUTTON_COLORS.PRIMARY}
            className={classes.filterButton}
          />
        </Box>
      </Box>

      {appliedFilterLabels.length > 0 && (
        <Box className={classes.chipsContainer}>
          {appliedFilterLabels.map((label) => (
            <MiChip
              key={label}
              label={label}
              size="small"
              variant="outlined"
              className={classes.chip}
            />
          ))}
        </Box>
      )}

      {showFilters && (
        <Box className={classes.bottomRow}>
          <CalendarFilterGroup
            filters={filters}
            builderOptions={builderOptions}
            communityOptions={communityOptions}
            statusOptions={statusOptions}
            hasPendingFilters={hasPendingFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            onToggleFilters={toggleFilters}
            handleFilterChange={handleFilterChange}
            layout="stacked"
          />
        </Box>
      )}

      <Box className={classes.listContainer}>
        {isLoading ? (
          <Box className={classes.loaderContainer}>
            <MiLoader isLoading type={LoaderType.Linear} />
          </Box>
        ) : (
          <UnassignedWorkOrdersList
            workOrders={visibleWorkOrders}
            loading={isLoading}
            onWorkOrderClick={onWorkOrderClick}
            zoom={listZoom}
          />
        )}
      </Box>
    </Box>
  );
};

type UnassignedWorkOrdersProps = {
  stagedAttemptIds?: ReadonlySet<string>;
  confirmedAttemptIds?: ReadonlySet<string>;
  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    padding: theme.spacing(1.5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    borderRadius: 4,
    flex: 1,
    overflow: "auto",
  },
  topRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  controlsRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  titleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  filterButton: {
    height: 24,
  },
  zoomInput: {
    "& .MuiSelect-select": {
      height: "24px !important",
      fontSize: theme.fontSize.small,
    },
  },
  bottomRow: {
    display: "flex",
    flexDirection: "column",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
  },
  chip: {
    height: 20,
    fontSize: 11,
    borderRadius: 2,
    backgroundColor: theme.palette.grey.lightAccent,
  },
  countChip: {
    height: 20,
    minWidth: 16,
    fontSize: 11,
    color: theme.palette.common.white,
    backgroundColor: theme.palette.error.main,
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
  listContainer: {
    display: "flex",
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    backgroundColor: theme.palette.blue.light,
    borderRadius: theme.spacing(1),
  },
}));
